import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { useApiAction } from "@redriver/cinnamon";
import { SystemRoutes } from "constants/routes";
import { getSsoConfig } from "./actions";
import MicrosoftSignInImage from "assets/auth/ms-sign-in.svg";

const LEADING_SLASH = /^\//g;
const TRAILING_SLASH = /\/$/g;

const PKCE_VERIFIER_STORAGE_KEY = "microsoft_pkce_verifier";
const PKCE_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

const generateRandomString = (length) => {
  const array = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(array, (b) => PKCE_CHARS[b % PKCE_CHARS.length]).join("");
};

const generatePkce = async () => {
  const codeVerifier = generateRandomString(64);
  const data = new TextEncoder().encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
  return { codeVerifier, codeChallenge };
};

const AzureSsoSignInButton = () => {
  const location = useLocation();
  const [{ response }] = useApiAction(getSsoConfig, { lazy: false });
  const clientId = response?.microsoft?.appId;
  const [redirecting, setRedirecting] = useState(false);
  const disabled = !clientId || redirecting;

  const handleClick = async () => {
    // disable the button and show a spinner straight away, since generating
    // the PKCE challenge below is async and would otherwise leave the button
    // looking inert for a brief but noticeable moment after being clicked
    setRedirecting(true);

    let codeVerifier;
    let codeChallenge;
    try {
      ({ codeVerifier, codeChallenge } = await generatePkce());
    } catch (error) {
      // e.g. Web Crypto unavailable in a non-secure context, or an
      // unsupported browser - let the user retry instead of leaving the
      // button stuck disabled with a spinner that never resolves
      console.error("Failed to generate PKCE challenge", error);
      setRedirecting(false);
      return;
    }

    sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, codeVerifier);

    const redirectUri =
      window.location.origin.replace(TRAILING_SLASH, "") +
      "/" +
      SystemRoutes.AzureAdSignIn.replace(LEADING_SLASH, "");

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      response_mode: "query",
      scope: "openid email offline_access profile",
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    // PrivateRoute sets state.nextPath to the whole location object it was redirected from -
    // reduce it to a path string here so it survives the round trip through Microsoft's
    // "state" query param intact
    const nextLocation = location.state?.nextPath;
    const nextPath = nextLocation
      ? nextLocation.pathname +
        (nextLocation.search || "") +
        (nextLocation.hash || "")
      : null;
    if (nextPath) {
      // URLSearchParams percent-encodes values itself when serialised via
      // .toString() below - encoding here too would double-encode it
      params.set("state", nextPath);
    }

    window.location.href = `https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize?${params.toString()}`;
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        style={{
          display: "inline-block",
          padding: 0,
          border: "none",
          background: "none",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <img src={MicrosoftSignInImage} alt="Sign in with Microsoft" />
      </button>
      {redirecting && (
        <Loader active inline size="small" style={{ marginLeft: "10px" }} />
      )}
    </span>
  );
};

export default AzureSsoSignInButton;
